<template>
  <v-dialog
    max-width="800"
    :value="value"
    persistent
    :fullscreen="$vuetify.breakpoint.smAndDown"
    @keydown.esc="close(employee)"
  >
    <v-card tile v-if="value">
      <v-app-bar dark flat color="primary">
        <v-btn icon dark @click="close(employee)">
          <v-icon>close</v-icon>
        </v-btn>
        <span class="title">{{
          employee && employee._id
            ? resx.labels.updateContractorEmployee
            : resx.labels.newContractorEmployees
        }}</span>
      </v-app-bar>

      <form @submit.prevent="save()" novalidate>
        <v-card-text>
          <v-container grid-list-xl>
            <v-row wrap>
              <v-col cols="12" md="6">
                <v-text-field
                  :label="resx.labels.firstName"
                  v-model="employee.firstName"
                  name="firstName"
                  :data-vv-as="resx.labels.firstName"
                  v-validate.initial="`required`"
                  :error-messages="errors.collect('firstName')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  :label="resx.labels.lastName"
                  v-model="employee.lastName"
                  name="lastName"
                  :data-vv-as="resx.labels.lastName"
                  v-validate.initial="`required`"
                  :error-messages="errors.collect('lastName')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  data-vv-name="employeesType"
                  :data-vv-as="resx.labels.employeesType"
                  :error-messages="errors.collect('employeesType')"
                  v-validate.initial="'required'"
                  :items="employeeTypes"
                  v-model="employee.type"
                  :label="resx.labels.employeesType"
                  item-text="name"
                  return-object
                  cache-items
                />
              </v-col>
              <v-col cols="12" md="6">
                <datepicker-component
                  :value="employee.birthDate"
                  data-vv-as="birthDate"
                  data-vv-name="birthDate"
                  v-validate.initial="'required'"
                  @input="employee.birthDate = $event"
                  :label="resx.labels.birthDate"
                  :max="new Date().toISOString().substr(0, 10)"
                  min="1950-01-01"
                  name="'birthDate'"
                  required="true"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  :label="resx.labels.contactPhone"
                  v-model="employee.contactPhone"
                  name="contactPhone"
                  :data-vv-as="resx.labels.contactPhone"
                  :error-messages="errors.collect('contactPhone')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  :label="resx.labels.contactEmail"
                  v-model="employee.contactEmail"
                  name="contactEmail"
                  v-validate="'email'"
                  :data-vv-as="resx.labels.contactEmail"
                  :error-messages="errors.collect('contactEmail')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-row row wrap>
                  <v-col d-flex xs4 sm4 md4>
                    <v-autocomplete
                      data-vv-name="identifierType"
                      :data-vv-as="resx.labels.identifierType"
                      :error-messages="errors.collect('identifierType')"
                      v-validate.initial="'required'"
                      :items="identifierTypes"
                      v-model="employee.identifier.type"
                      :label="resx.labels.identifierType"
                      item-text="text"
                      item-value="id"
                      cache-items
                    />
                  </v-col>
                  <v-col xs8>
                    <v-text-field
                      :label="resx.labels.identifier"
                      v-model="employee.identifier.number"
                      name="identifierNumber"
                      :data-vv-as="resx.labels.identifier"
                      :disabled="employee.identifier.type == undefined"
                      type="text"
                      return-masked-value
                      :mask="identifierMask"
                      v-validate.initial="{
                        required: true,
                        uniqueid: [contractorId, employee._id],
                        identifierValid: [employee.identifier.type]
                      }"
                      :error-messages="errors.collect('identifierNumber')"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="6">
                <v-col>
                  <v-row>
                    <comments-component
                    v-model="employee.comments"
                    mode="edit"
                    :label="commentsLabel"
                    @input="employee.comments = $event"
                    />
                  </v-row>
                </v-col>
              </v-col>
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="employee.isSubcontracted"
                  label="Subcontratado"
                  hint="Sale en su propia fila del reporte de fin de mes, debajo de la contratista."
                  persistent-hint
                  dense
                  class="mt-0"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="close(employee)">{{ resx.labels.cancel }}</v-btn>
          <v-btn class="primary" type="submit" :disabled="!isValidForm">{{
            resx.labels.ok
          }}</v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
"use strict";

import contractorCompanyService from "@/services/ContractorCompanyService";
import employeeTypeService from "@/services/EmployeeTypeService";
import store from "@/store/store";
import validations from "@/utils/validations";
import { sprintf } from "sprintf-js";
import datepickerComponent from "@/components/common/datepicker.vue";
import commentsComponent from "@/components/common/comments.vue";
import roles from "@/utils/roles";

const uniqueid = {
  getMessage() {
    return store.state.locale.resx.messages.uniqueIdentifier;
  },
  async validate(value, args) {
    const isValid = await contractorCompanyService.isValidEmployeeId(
      args[0],
      args[1],
      value
    );
    return {
      valid: isValid ? isValid.result : false
    };
  }
};

const identifierValid = {
  getMessage() {
    return store.state.locale.resx.messages.identifierInvalid;
  },
  validate(value, args) {
    return validations.isValidIdentifer(value, args);
  }
};

export default {
  name: "edit-contractor-employees",
  async mounted() {
    this.$validator.extend("uniqueid", uniqueid);
    this.$validator.extend("identifierValid", identifierValid);
    this.employeeTypes = await employeeTypeService.list();
    this.identifierTypes = [
      {
        id: store.state.constants.IDENTIFIERS.CUIT,
        text: store.state.constants.IDENTIFIERS.CUIT.toUpperCase()
      },
      {
        id: store.state.constants.IDENTIFIERS.CUIL,
        text: store.state.constants.IDENTIFIERS.CUIL.toUpperCase()
      },
      {
        id: store.state.constants.IDENTIFIERS.DNI,
        text: store.state.constants.IDENTIFIERS.DNI.toUpperCase()
      }
    ];
  },
  components: {
    datepickerComponent,
    commentsComponent
  },
  computed: {
    // Los comentarios los ve la empresa contratista; el personal del sistema
    // lo tiene que saber al editarlos.
    commentsLabel() {
      return roles.loggedUserHasRoles(
        this.$store.state.auth.user.roles,
        this.$store.state.constants.roles.ALL_SYS_ROLES
      )
        ? this.resx.labels.comments_visible_to_company
        : this.resx.labels.comments;
    }
  },
  data() {
    return {
      employeeTypes: [],
      identifierTypes: [],
      identifierMask: "",
      employee: {},
      originalEmployee: {}
    };
  },
  methods: {
    async save() {
      let payload = Object.assign({}, this.employee);
      if (!this.employee._id) {
        this.$emit("save", { isNew: true, item: payload });
      } else {
        this.$emit("save", { isNew: false, item: payload });
      }
    },
    async close(employee) {
      if (JSON.stringify(this.originalEmployee) != JSON.stringify(this.employee)) {
        await this.botConfirmDialog({
          title: `${
          this.resx.labels.edit
          } ${this.resx.labels.employee.toLowerCase()}`,
          content: sprintf(
          this.resx.messages.noSavedChanges,
          employee.name
          )
        });
        this.$emit("close");
      } else {
        this.$emit("close");
      }
    },
    saveDate(date) {
      this.$set(this.employee, "birthDate", date);
    },
    assignMask(value) {
      switch (value) {
        case store.state.constants.IDENTIFIERS.CUIT:
          this.identifierMask = "##-########-#";
          break;
        case store.state.constants.IDENTIFIERS.CUIL:
          this.identifierMask = "##-########-#";
          break;
        case store.state.constants.IDENTIFIERS.DNI:
          this.identifierMask = "########";
          break;
        default:
          this.identifierMask = "";
          break;
      }
    }
  },
  props: ["value", "contractorId"],
  watch: {
    value(val) {
      let data =
        val && val.selectedItem ? JSON.parse(JSON.stringify(val.selectedItem))
          : { identifier: {} };
      if (!data.identifier) {
        data.identifier = {};
      }
      this.employee = data;
      this.originalEmployee = Object.assign({}, this.employee);
    },
    menu(val) {
      val && this.$nextTick(() => (this.$refs.picker.activePicker = "Año"));
    },
    "employee.identifier.type"(val) {
      if (val) {
        this.assignMask(val);
      }
    }
  }
};
</script>
